/**
 * TranslationProvider - Provides translations with fallback logic
 *
 * Per R-4: Single Responsibility - translation retrieval only
 * Fallback chain: requested locale → English → formatted key
 *
 * @module services/localization
 */
import { CONSOLIDATED_DYES, CONSOLIDATED_IDS, } from '../../config/consolidated-ids.js';
/**
 * Provides translations with automatic fallback to English
 *
 * Fallback chain: requested locale → English → formatted key / original value
 *
 * BUG-007: every lookup keeps a truthiness check, which treats an empty string
 * ("") as a missing translation and falls back to English. That is intentional —
 * an empty string in a locale file means "not translated yet", and the Korean
 * and Chinese locales are manually sourced with incomplete coverage. For
 * detecting incomplete locale files at build time, see `build-locales.ts`.
 *
 * BUG-105: every lookup is ALSO guarded with `Object.hasOwn`, because a bare
 * truthiness check on a plain object resolves through `Object.prototype` — a
 * key of `'constructor'` or `'toString'` is truthy and returns a FUNCTION where
 * a localized string is expected. FINDING-027 added that guard to `getLabel`
 * alone; its ten siblings went without one until this pass.
 */
export class TranslationProvider {
    registry;
    constructor(registry) {
        this.registry = registry;
    }
    /**
     * Get UI label with fallback chain
     *
     * @param key - Translation key
     * @param locale - Requested locale
     * @returns Translated label or formatted key
     *
     * @example
     * ```typescript
     * const label = provider.getLabel('dye', 'ja');
     * // Returns "カララント:" (ja) or "Dye" (en fallback)
     * ```
     */
    getLabel(key, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale (FINDING-027: own-property lookups — a key such as
        // "constructor" must not resolve through the prototype chain)
        if (localeData && Object.hasOwn(localeData.labels, key) && localeData.labels[key]) {
            return localeData.labels[key];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData && Object.hasOwn(englishData.labels, key) && englishData.labels[key]) {
                return englishData.labels[key];
            }
        }
        // Final fallback: format key (camelCase → Title Case)
        return this.formatKey(key);
    }
    /**
     * Get dye name with fallback
     *
     * @param itemID - Dye item ID
     * @param locale - Requested locale
     * @returns Localized dye name or null if not found
     *
     * @example
     * ```typescript
     * const name = provider.getDyeName(5729, 'ja');
     * // Returns "スノウホワイト" (ja) or "Snow White" (en fallback)
     * ```
     */
    getDyeName(itemID, locale) {
        const localeData = this.registry.getLocale(locale);
        const idStr = String(itemID);
        // Try requested locale
        if (localeData?.dyeNames && Object.hasOwn(localeData.dyeNames, idStr) && localeData?.dyeNames[idStr]) {
            return localeData.dyeNames[idStr];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.dyeNames && Object.hasOwn(englishData.dyeNames, idStr) && englishData?.dyeNames[idStr]) {
                return englishData.dyeNames[idStr];
            }
        }
        // BUG-002 (2026-04-28 audit): Patch 7.5 consolidated dye items live in
        // CONSOLIDATED_DYES, not in the CSV-driven locale registry. Fall back
        // to that source so a caller asking for "what's the name of itemID
        // 52254 in ja?" gets "カララント:ノーマルカラー" instead of null.
        for (const type of ['A', 'B', 'C']) {
            if (CONSOLIDATED_IDS[type] === itemID) {
                const names = CONSOLIDATED_DYES[type].names;
                return names[locale] ?? names.en;
            }
        }
        return null;
    }
    /**
     * Get category name with fallback
     *
     * @param category - Category key (e.g., "Reds", "Blues")
     * @param locale - Requested locale
     * @returns Localized category name
     *
     * @example
     * ```typescript
     * const category = provider.getCategory('Reds', 'ja');
     * // Returns "赤系" (ja) or "Reds" (en fallback)
     * ```
     */
    getCategory(category, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.categories && Object.hasOwn(localeData.categories, category) && localeData?.categories[category]) {
            return localeData.categories[category];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.categories && Object.hasOwn(englishData.categories, category) && englishData?.categories[category]) {
                return englishData.categories[category];
            }
        }
        // Final fallback: return original category
        return category;
    }
    /**
     * Get acquisition method with fallback
     *
     * @param acquisition - Acquisition key (e.g., "Dye Vendor", "Crafting")
     * @param locale - Requested locale
     * @returns Localized acquisition method
     *
     * @example
     * ```typescript
     * const acq = provider.getAcquisition('Dye Vendor', 'ja');
     * // Returns "染料販売業者" (ja) or "Dye Vendor" (en fallback)
     * ```
     */
    getAcquisition(acquisition, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.acquisitions && Object.hasOwn(localeData.acquisitions, acquisition) && localeData?.acquisitions[acquisition]) {
            return localeData.acquisitions[acquisition];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.acquisitions && Object.hasOwn(englishData.acquisitions, acquisition) && englishData?.acquisitions[acquisition]) {
                return englishData.acquisitions[acquisition];
            }
        }
        // Final fallback: return original acquisition
        return acquisition;
    }
    /**
     * Get localized currency display label
     *
     * @param currency - Currency key (e.g., "Gil", "Cosmocredits")
     * @param locale - Requested locale
     * @returns Localized currency label
     *
     * @example
     * ```typescript
     * const cur = provider.getCurrency('Skybuilders Scrips', 'ja');
     * // Returns "振興券" (ja) or "Scrips" (en fallback)
     * ```
     */
    getCurrency(currency, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.currencies && Object.hasOwn(localeData.currencies, currency) && localeData.currencies[currency]) {
            return localeData.currencies[currency];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.currencies && Object.hasOwn(englishData.currencies, currency) && englishData.currencies[currency]) {
                return englishData.currencies[currency];
            }
        }
        // Final fallback: return original currency
        return currency;
    }
    /**
     * Get harmony type with fallback
     *
     * @param key - Harmony type key
     * @param locale - Requested locale
     * @returns Localized harmony type name
     *
     * @example
     * ```typescript
     * const harmony = provider.getHarmonyType('triadic', 'ja');
     * // Returns "三色配色" (ja) or "Triadic" (en fallback)
     * ```
     */
    getHarmonyType(key, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.harmonyTypes && Object.hasOwn(localeData.harmonyTypes, key) && localeData?.harmonyTypes[key]) {
            return localeData.harmonyTypes[key];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.harmonyTypes && Object.hasOwn(englishData.harmonyTypes, key) && englishData?.harmonyTypes[key]) {
                return englishData.harmonyTypes[key];
            }
        }
        // Final fallback: format key
        return this.formatKey(key);
    }
    /**
     * Localised display name of a Harmony Explorer colour wheel, with English
     * fallback and finally the id itself, mirroring {@link getHarmonyType}.
     */
    getColorWheelName(id, locale) {
        const pick = (data) => data?.colorWheels && Object.hasOwn(data.colorWheels, id) ? data.colorWheels[id] : undefined;
        // I18N-007: match every sibling getter's final fallback — `formatKey(id)`
        // renders "Oklch-hue" for an unrecognised id, not the raw "oklch-hue".
        return pick(this.registry.getLocale(locale)) ?? pick(this.registry.getLocale('en')) ?? this.formatKey(id);
    }
    /**
     * Get vision type with fallback
     *
     * @param key - Vision type key
     * @param locale - Requested locale
     * @returns Localized vision type name
     *
     * @example
     * ```typescript
     * const vision = provider.getVisionType('deuteranopia', 'ja');
     * // Returns "2型色覚（赤緑色盲）" (ja) or English fallback
     * ```
     */
    getVisionType(key, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.visionTypes && Object.hasOwn(localeData.visionTypes, key) && localeData?.visionTypes[key]) {
            return localeData.visionTypes[key];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.visionTypes && Object.hasOwn(englishData.visionTypes, key) && englishData?.visionTypes[key]) {
                return englishData.visionTypes[key];
            }
        }
        // Final fallback: format key
        return this.formatKey(key);
    }
    /**
     * Get short vision-name with fallback
     *
     * Use this for compact UI surfaces like OG embed titles where the verbose
     * `getVisionType()` form ("Deuteranopia (Red-Green Colorblindness)") is
     * too long. Returns just the medical term, e.g. "Deuteranopia".
     *
     * @param key - Vision type key
     * @param locale - Requested locale
     * @returns Localized short vision name
     *
     * @example
     * ```typescript
     * const v = provider.getVisionShort('deuteranopia', 'ja');
     * // Returns "2型色覚" (ja) or "Deuteranopia" (en fallback)
     * ```
     */
    getVisionShort(key, locale) {
        const localeData = this.registry.getLocale(locale);
        if (localeData?.visions && Object.hasOwn(localeData.visions, key) && localeData.visions[key]) {
            return localeData.visions[key];
        }
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.visions && Object.hasOwn(englishData.visions, key) && englishData.visions[key]) {
                return englishData.visions[key];
            }
        }
        return this.formatKey(key);
    }
    /**
     * Get tool display name with fallback
     *
     * Used by og-worker for shareable link previews and any UI that lists
     * the available web-app tools by name.
     *
     * @param key - Tool key
     * @param locale - Requested locale
     * @returns Localized tool display name
     *
     * @example
     * ```typescript
     * const t = provider.getToolName('harmony', 'ja');
     * // Returns "ハーモニーエクスプローラー" (ja) or "Harmony Explorer" (en fallback)
     * ```
     */
    getToolName(key, locale) {
        const localeData = this.registry.getLocale(locale);
        if (localeData?.tools && Object.hasOwn(localeData.tools, key) && localeData.tools[key]) {
            return localeData.tools[key];
        }
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.tools && Object.hasOwn(englishData.tools, key) && englishData.tools[key]) {
                return englishData.tools[key];
            }
        }
        return this.formatKey(key);
    }
    /**
     * Get color-sheet category name with fallback
     *
     * Color sheets are FFXIV character-creator color groups exposed by the
     * Swatch Matcher tool (eye colors, lip colors, hair colors, etc.).
     *
     * @param key - Sheet key
     * @param locale - Requested locale
     * @returns Localized sheet name
     *
     * @example
     * ```typescript
     * const s = provider.getSheetName('eyeColors', 'ja');
     * // Returns "目の色" (ja) or "Eye Colors" (en fallback)
     * ```
     */
    getSheetName(key, locale) {
        const localeData = this.registry.getLocale(locale);
        if (localeData?.sheets && Object.hasOwn(localeData.sheets, key) && localeData.sheets[key]) {
            return localeData.sheets[key];
        }
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.sheets && Object.hasOwn(englishData.sheets, key) && englishData.sheets[key]) {
                return englishData.sheets[key];
            }
        }
        return this.formatKey(key);
    }
    /**
     * Get race name with fallback
     *
     * @param key - Race key
     * @param locale - Requested locale
     * @returns Localized race name
     *
     * @example
     * ```typescript
     * const race = provider.getRace('miqote', 'ja');
     * // Returns "ミコッテ" (ja) or "Miqo'te" (en fallback)
     * ```
     */
    getRace(key, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.races && Object.hasOwn(localeData.races, key) && localeData.races[key]) {
            return localeData.races[key];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.races && Object.hasOwn(englishData.races, key) && englishData.races[key]) {
                return englishData.races[key];
            }
        }
        // Final fallback: format key
        return this.formatKey(key);
    }
    /**
     * Get clan (subrace) name with fallback
     *
     * @param key - Clan key
     * @param locale - Requested locale
     * @returns Localized clan name
     *
     * @example
     * ```typescript
     * const clan = provider.getClan('seekerOfTheSun', 'ja');
     * // Returns "サンシーカー" (ja) or "Seeker of the Sun" (en fallback)
     * ```
     */
    getClan(key, locale) {
        const localeData = this.registry.getLocale(locale);
        // Try requested locale
        if (localeData?.clans && Object.hasOwn(localeData.clans, key) && localeData.clans[key]) {
            return localeData.clans[key];
        }
        // Fallback to English
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.clans && Object.hasOwn(englishData.clans, key) && englishData.clans[key]) {
                return englishData.clans[key];
            }
        }
        // Final fallback: format key
        return this.formatKey(key);
    }
    /**
     * Get a localized Facewear tint name by its slug.
     *
     * @example
     * ```ts
     * const name = provider.getFacewearColorName('brass', 'ja');
     * // Returns "ブラス" (ja) or "Brass" (en fallback)
     * ```
     */
    getFacewearColorName(id, locale) {
        const localeData = this.registry.getLocale(locale);
        if (localeData?.facewearColors &&
            Object.hasOwn(localeData.facewearColors, id) &&
            localeData.facewearColors[id]) {
            return localeData.facewearColors[id];
        }
        if (locale !== 'en') {
            const englishData = this.registry.getLocale('en');
            if (englishData?.facewearColors &&
                Object.hasOwn(englishData.facewearColors, id) &&
                englishData.facewearColors[id]) {
                return englishData.facewearColors[id];
            }
        }
        return this.formatKey(id);
    }
    /**
     * Format camelCase/PascalCase key to Title Case
     *
     * @param key - Key to format
     * @returns Formatted string
     * @private
     *
     * @example
     * ```typescript
     * formatKey('splitComplementary') // "Split Complementary"
     * formatKey('cosmicExploration') // "Cosmic Exploration"
     * ```
     */
    formatKey(key) {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    }
}
//# sourceMappingURL=TranslationProvider.js.map